'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import { DataProvider, useDataProvider } from '@effektify/data-provider';
import { isHeroOverviewConfiguration } from '@effektify/types/guards';
import { useState, useEffect } from 'react';
import { Wizard } from 'src/components/Organisms/Wizard';
import useAuth from 'src/hooks/useAuth';
import useUserDetails from 'src/hooks/useUserDetails';
import useReports from 'src/hooks/useReports';
import usePageTitle from 'src/hooks/usePageTitle';
import useTeams from 'src/hooks/useTeams';
import useRouteWithParams from 'src/hooks/useRouteWithParams';
import { PageHeader } from 'src/components/Organisms/PageHeader';
import { Report } from 'src/components/Organisms/Report';
import { ReportForm } from 'src/components/Organisms/ReportForm';
import { ReportWrapper } from 'src/components/Organisms/ReportWrapper';
import { HeaderWithForm, HeroOverview } from './components';
import { FlexRow } from 'src/components/Atoms/FlexBox';
import { Share } from 'src/components/Molecules/Share/Share';
import { styled } from '@mui/material/styles';
import { ReportHelp } from 'src/components/Organisms/ReportHelp/ReportHelp';
import useTranslations from 'src/hooks/useTranslations';
import useIntegrations from 'src/hooks/useIntegrations';
import { useRouter } from 'next/navigation';
import { ROUTES } from 'src/constants/routes';
const FormWrapper = styled(FlexRow)(({
  theme
}) => ({
  paddingLeft: theme.sizes.spacing['10'],
  gap: 0
}));
const ActionsBox = styled(FlexRow)(({
  theme
}) => ({
  ':not(:empty)': {
    paddingLeft: theme.sizes.spacing['06'],
    marginLeft: theme.sizes.spacing['06'],
    display: 'flex',
    flexDirection: 'row',
    gap: theme.sizes.spacing['03'],
    borderLeft: `1px solid ${theme.colors.border.secondary}`,
    alignSelf: 'end'
  }
}));
const RegularOverview: React.FC = () => {
  const $ = _c(23);
  const {
    userDetails
  } = useUserDetails();
  const {
    t
  } = useTranslations();
  const {
    report,
    configuration,
    isLoading
  } = useDataProvider();
  if (!userDetails) {
    return null;
  }
  let t0;
  if ($[0] !== configuration?.title || $[1] !== t) {
    t0 = configuration?.title ?? t("reportPage.overviewTitle");
    $[0] = configuration?.title;
    $[1] = t;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ReportForm />;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== configuration || $[5] !== isLoading) {
    t2 = configuration?.id && !isLoading && <Share reportId={configuration.id} />;
    $[4] = configuration;
    $[5] = isLoading;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== isLoading) {
    t3 = !isLoading && <ReportHelp />;
    $[7] = isLoading;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2 || $[10] !== t3) {
    t4 = <FormWrapper>{t1}<ActionsBox>{t2}{t3}</ActionsBox></FormWrapper>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t0 || $[13] !== t4 || $[14] !== userDetails) {
    t5 = <PageHeader><HeaderWithForm profile={userDetails} reportName={t0}>{t4}</HeaderWithForm></PageHeader>;
    $[12] = t0;
    $[13] = t4;
    $[14] = userDetails;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== isLoading || $[17] !== report) {
    t6 = <ReportWrapper><Report report={report} isLoading={isLoading} /></ReportWrapper>;
    $[16] = isLoading;
    $[17] = report;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Wizard />;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t5 || $[21] !== t6) {
    t8 = <>{t5}{t6}{t7}</>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
const OverviewPage: React.FC = () => {
  const $ = _c(25);
  const {
    dashboard
  } = useReports();
  const {
    userDetails
  } = useUserDetails();
  const {
    t
  } = useTranslations();
  const {
    currentTeam
  } = useTeams();
  const {
    integrations,
    isLoadingIntegrations
  } = useIntegrations();
  const {
    updatePathWithParams
  } = useRouteWithParams();
  const router = useRouter();
  const {
    configuration,
    isConfigLoading
  } = useDataProvider();
  let t0;
  if ($[0] !== configuration) {
    t0 = isHeroOverviewConfiguration(configuration);
    $[0] = configuration;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isHeroOverview = t0;
  const isConfigPending = isConfigLoading || !!dashboard?.url && !configuration;
  let t1;
  if ($[2] !== currentTeam?.id || $[3] !== currentTeam?.integration_responsibility || $[4] !== integrations || $[5] !== isConfigPending || $[6] !== isHeroOverview || $[7] !== isLoadingIntegrations || $[8] !== router || $[9] !== updatePathWithParams) {
    t1 = () => {
      if (isConfigPending || isHeroOverview) {
        return;
      }
      if (integrations && !isLoadingIntegrations && Object.keys(integrations).length === 0 && currentTeam?.integration_responsibility === "customer") {
        router.push(updatePathWithParams(ROUTES.ONBOARDING_CONNECTIONS, {
          companyId: currentTeam?.id || ""
        }));
      }
    };
    $[2] = currentTeam?.id;
    $[3] = currentTeam?.integration_responsibility;
    $[4] = integrations;
    $[5] = isConfigPending;
    $[6] = isHeroOverview;
    $[7] = isLoadingIntegrations;
    $[8] = router;
    $[9] = updatePathWithParams;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== currentTeam || $[12] !== integrations || $[13] !== isConfigPending || $[14] !== isHeroOverview || $[15] !== isLoadingIntegrations || $[16] !== router || $[17] !== updatePathWithParams) {
    t2 = [integrations, isLoadingIntegrations, currentTeam, router, updatePathWithParams, isConfigPending, isHeroOverview];
    $[11] = currentTeam;
    $[12] = integrations;
    $[13] = isConfigPending;
    $[14] = isHeroOverview;
    $[15] = isLoadingIntegrations;
    $[16] = router;
    $[17] = updatePathWithParams;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  useEffect(t1, t2);
  let t3;
  if ($[19] !== configuration?.title || $[20] !== t) {
    t3 = configuration?.title ?? t("reportPage.overviewTitle");
    $[19] = configuration?.title;
    $[20] = t;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      teamSuffix: true
    };
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  usePageTitle(t3, t4);
  if (!userDetails || !dashboard || isConfigPending) {
    return null;
  }
  let t5;
  if ($[23] !== isHeroOverview) {
    t5 = isHeroOverview ? <HeroOverview /> : <RegularOverview />;
    $[23] = isHeroOverview;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
};
const OverviewWithDataProvider: React.FC = () => {
  const $ = _c(11);
  const [configURI, setConfigURI] = useState("");
  const {
    dashboard,
    updateCurrentReport
  } = useReports();
  const {
    getToken
  } = useAuth();
  let t0;
  if ($[0] !== dashboard) {
    t0 = () => {
      if (dashboard?.url) {
        setConfigURI(prev => prev === dashboard.url ? prev : dashboard.url);
      }
    };
    $[0] = dashboard;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== dashboard || $[3] !== updateCurrentReport) {
    t1 = [dashboard, updateCurrentReport];
    $[2] = dashboard;
    $[3] = updateCurrentReport;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  if ($[5] !== configURI || $[6] !== getToken) {
    t2 = {
      getToken,
      configURI
    };
    $[5] = configURI;
    $[6] = getToken;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <OverviewPage />;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t2) {
    t4 = <DataProvider config={t2}>{t3}</DataProvider>;
    $[9] = t2;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
export const dynamic = 'force-dynamic'; // This is to ensure the page is not cached by Next.js
export default OverviewWithDataProvider;